import z from 'zod';

const appPasswordAccessTokenRequestSchema = z
    .object({
        grant_type: z.literal('app_password'),
        client_id: z.string(),
        username: z.string(),
        password: z.string(),
        country: z.string().optional(),
        turnstile_token: z.string(),
    })
    .describe('AppPasswordAccessTokenRequest');

const installationIdAccessTokenRequestSchema = z
    .object({
        grant_type: z.literal('installation_id'),
        client_id: z.string(),
        installation_id: z.string(),
        email: z.string().optional(),
        country: z.string().optional(),
        language: z.string().optional(),
    })
    .describe('InstallationIdAccessTokenRequest');

const authorizationCodeAccessTokenRequestSchema = z
    .object({
        grant_type: z.literal('authorization_code'),
        client_id: z.string(),
        code: z.string(),
        redirect_uri: z.string(),
        code_verifier: z.string(),
    })
    .describe('AuthorizationCodeAccessTokenRequest');

export const accessTokenRequestSchema = z
    .discriminatedUnion('grant_type', [
        appPasswordAccessTokenRequestSchema,
        installationIdAccessTokenRequestSchema,
        authorizationCodeAccessTokenRequestSchema,
    ])
    .describe('AccessTokenRequest');

export type AccessTokenRequest = z.infer<typeof accessTokenRequestSchema>;

export const accessTokenResponseSchema = z
    .object({
        token_type: z.string(),
        access_token: z.string(),
        refresh_token: z.string().optional(),
        user_id: z.string(),
        expires_in: z.number(),
        client_params: z.unknown(),
        /** Only reported on authorization_code exchanges: whether the sign-in registered a new account. */
        account_created: z.boolean().optional(),
    })
    .describe('AccessTokenResponse');

export type AccessTokenResponse = z.infer<typeof accessTokenResponseSchema>;

export interface AccessToken {
    readonly accessToken: string;
    readonly refreshToken?: string;
    readonly userId: string;
    /** Unix seconds. */
    readonly expiresAt: number;
    readonly clientParams: object;
    readonly accountCreated?: boolean;
}

/**
 * Maps the payload to the shape the app stores. This isn't a schema transform because a
 * mismatch outside strict validation passes the raw payload through untransformed.
 */
export function toAccessToken(response: AccessTokenResponse, now = Date.now()): AccessToken {
    if (response.token_type.toLowerCase() !== 'bearer') {
        throw new Error(`Expected bearer token type, got ${response.token_type}.`);
    }

    return {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        userId: response.user_id,
        expiresAt: Math.floor(now / 1000) + response.expires_in,
        clientParams:
            typeof response.client_params === 'object' && response.client_params !== null ? response.client_params : {},
        accountCreated: response.account_created,
    };
}
